/**
 * useEntityJourneyGraph — Fetch journey graph for N selected actors.
 *
 * DRR-126 Phase 5: Powers the NeuralJourneyGraph 3D renderer. Given a list of
 * actor IDs (residents and/or staff), returns all tasks they were involved in
 * as nodes, with temporal edges (same actor, time sequence) and intersection
 * edges (different actors who shared the same care event instance_id).
 *
 * Intersection nodes are the moments two lives touched — resident + CNA in
 * the same medication pass, DON + ALD in the same incident review. These
 * render as highlighted nodes in the 3D graph.
 *
 * Data tier: T2 (operational). No clinical content — task metadata only.
 * Backend enforces scope per DRR-102 + DRR-114.
 *
 * DRR-105: actorId fields are NEVER returned by the backend. The backend
 * resolves actor display names from DDB and returns `actorName`/`actorNames`.
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const ENTITY_JOURNEY_GRAPH_QUERY = gql`
  query EntityJourneyGraph($actorIds: [String!]!, $limit: Int) {
    entityJourneyGraph(actorIds: $actorIds, limit: $limit) {
      actorNames
      totalNodes
      intersectionInstanceIds
      nodes {
        nodeId
        taskId
        taskName
        taskType
        status
        actorName
        actorRole
        timestamp
        constellationId
        instanceId
      }
      edges {
        sourceNodeId
        targetNodeId
        edgeType
        weight
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface JourneyNode {
  nodeId: string;
  taskId: string;
  taskName: string;
  taskType: string;
  /** pending | in_progress | completed | overdue */
  status: string;
  /** Display name resolved from DDB (DRR-105: never a raw sequential actor ID) */
  actorName: string;
  /** subject | performer */
  actorRole: string;
  /** ISO 8601 — temporal position in 3D graph Z-axis */
  timestamp: string;
  constellationId: string;
  instanceId: string;
}

export interface JourneyEdge {
  sourceNodeId: string;
  targetNodeId: string;
  /** temporal | intersection */
  edgeType: string;
  weight: number;
}

interface QueryData {
  entityJourneyGraph: {
    actorNames: string[];
    totalNodes: number;
    intersectionInstanceIds: string[];
    nodes: JourneyNode[];
    edges: JourneyEdge[];
  };
}

export interface EntityJourneyGraphResult {
  /** Actor display names (DRR-105: never raw sequential IDs) */
  actorNames: string[];
  nodes: JourneyNode[];
  edges: JourneyEdge[];
  /** Set of instance IDs shared by 2+ actors — the intersection moments */
  intersectionInstanceIds: Set<string>;
  totalNodes: number;
  /** Nodes keyed by nodeId for O(1) lookup */
  nodeById: Map<string, JourneyNode>;
  loading: boolean;
  error?: Error;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetch entity journey graph for N selected actors.
 *
 * @param actorIds - Actor IDs to include (up to 40). Empty array = skip.
 * @param limit    - Max tasks per actor (default 100, max 200)
 * @param skip     - Skip query
 */
export function useEntityJourneyGraph(
  actorIds: string[],
  limit?: number,
  skip = false,
): EntityJourneyGraphResult {
  const { data, loading, error } = useQuery<QueryData>(
    ENTITY_JOURNEY_GRAPH_QUERY,
    {
      variables: { actorIds, limit: limit ?? null },
      skip: skip || actorIds.length === 0,
      fetchPolicy: 'cache-and-network',
    },
  );

  const graph = data?.entityJourneyGraph;

  const nodeById = useMemo<Map<string, JourneyNode>>(() => {
    const map = new Map<string, JourneyNode>();
    for (const node of graph?.nodes ?? []) {
      map.set(node.nodeId, node);
    }
    return map;
  }, [graph?.nodes]);

  const intersectionInstanceIds = useMemo<Set<string>>(
    () => new Set(graph?.intersectionInstanceIds ?? []),
    [graph?.intersectionInstanceIds],
  );

  return {
    actorNames: graph?.actorNames ?? [],
    nodes: graph?.nodes ?? [],
    edges: graph?.edges ?? [],
    intersectionInstanceIds,
    totalNodes: graph?.totalNodes ?? 0,
    nodeById,
    loading,
    error: error as Error | undefined,
  };
}
